import {
  Bank,
  MoneyAdjustment,
  MoneyCollection,
  MoneyContainer,
  MoneyDay,
  MoneyDayCashSummary,
  MoneyDayPaymentTypeSummary,
  MoneyDayReconciliationSubmission,
  MoneyDayTerminalReconciliationLine,
  MoneyEvent,
  MoneyTransfer
} from 'src/typings';

export function toDate<T = Date | string | null | undefined>(value: T): T | Date {
  if (!value) return value as T;
  const normalized = new Date(value as Date | string);
  return Number.isNaN(normalized.getTime()) ? (value as T) : normalized;
}

export function toNumber(value: unknown): number {
  return Number(value || 0);
}

function toOptionalNumber(value: unknown): number | null | undefined {
  if (value === null || value === undefined) return value;
  return Number(value || 0);
}

function normalizeTerminalReconciliationLine(
  line: MoneyDayTerminalReconciliationLine
): MoneyDayTerminalReconciliationLine {
  const normalizeAmount = (value: unknown) =>
    value === null || value === undefined ? value : toNumber(value);

  return {
    ...line,
    id: normalizeAmount(line?.id) as number | undefined,
    payment_terminal_id: normalizeAmount(line?.payment_terminal_id) as number | null | undefined,
    terminal_profile_id: normalizeAmount(line?.terminal_profile_id ?? line?.payment_terminal_id) as
      | number
      | null
      | undefined,
    expected_sale_amount: normalizeAmount(line?.expected_sale_amount),
    expected_sale_count: normalizeAmount(line?.expected_sale_count),
    expected_refund_amount: normalizeAmount(line?.expected_refund_amount),
    expected_refund_count: normalizeAmount(line?.expected_refund_count),
    expected_void_amount: normalizeAmount(line?.expected_void_amount),
    expected_void_count: normalizeAmount(line?.expected_void_count),
    reported_sale_amount: normalizeAmount(line?.reported_sale_amount),
    reported_sale_count: normalizeAmount(line?.reported_sale_count),
    reported_refund_amount: normalizeAmount(line?.reported_refund_amount),
    reported_refund_count: normalizeAmount(line?.reported_refund_count),
    reported_void_amount: normalizeAmount(line?.reported_void_amount),
    reported_void_count: normalizeAmount(line?.reported_void_count),
    sale_variance_amount: normalizeAmount(line?.sale_variance_amount),
    sale_variance_count: normalizeAmount(line?.sale_variance_count),
    refund_variance_amount: normalizeAmount(line?.refund_variance_amount),
    refund_variance_count: normalizeAmount(line?.refund_variance_count),
    void_variance_amount: normalizeAmount(line?.void_variance_amount),
    void_variance_count: normalizeAmount(line?.void_variance_count),
    pending_operation_count: toNumber(line?.pending_operation_count),
    unbooked_operation_count: toNumber(line?.unbooked_operation_count),
    warnings: Array.isArray(line?.warnings) ? line.warnings.map((warning) => String(warning)) : [],
    reconciliation_warnings: Array.isArray(line?.reconciliation_warnings)
      ? line.reconciliation_warnings.map((warning) => String(warning))
      : [],
    confirmed_no_refunds: Boolean(line?.confirmed_no_refunds),
    legacy_unidentified: Boolean(line?.legacy_unidentified)
  };
}

function normalizeReconciliationSubmission(
  submission: MoneyDayReconciliationSubmission
): MoneyDayReconciliationSubmission {
  return {
    ...submission,
    id: toNumber(submission?.id),
    money_day_id: toNumber(submission?.money_day_id),
    revision: toNumber(submission?.revision),
    closed_ledger_entry_id: toOptionalNumber(submission?.closed_ledger_entry_id),
    submitted_by_user_id: toOptionalNumber(submission?.submitted_by_user_id),
    reviewed_by_user_id: toOptionalNumber(submission?.reviewed_by_user_id),
    boundary_at: toDate(submission?.boundary_at),
    submitted_at: toDate(submission?.submitted_at),
    reviewed_at: toDate(submission?.reviewed_at),
    created_at: toDate(submission?.created_at),
    lines: (submission?.lines || []).map((line) => normalizeTerminalReconciliationLine(line))
  };
}

export function normalizeMoneyEvent(event: MoneyEvent): MoneyEvent {
  return {
    ...event,
    operation_key: event?.operation_key || event?.operationKey || null,
    ledger_entry_id:
      event?.ledger_entry_id !== undefined && event?.ledger_entry_id !== null
        ? toNumber(event.ledger_entry_id)
        : event?.ledgerEntryId !== undefined && event?.ledgerEntryId !== null
        ? toNumber(event.ledgerEntryId)
        : event?.ledger_entry_id,
    amount_signed: toNumber(event?.amount_signed),
    perspective_amount_signed:
      event?.perspective_amount_signed !== undefined && event?.perspective_amount_signed !== null
        ? toNumber(event.perspective_amount_signed)
        : toNumber(event?.amount_signed),
    category: String(event?.category || '').trim() || null,
    comment: String(event?.comment || '').trim() || null,
    occurred_at: toDate(event?.occurred_at),
    business_date: toDate(event?.business_date)
  };
}

export function normalizeMoneyDayCashSummary(
  summary?: MoneyDayCashSummary | null
): MoneyDayCashSummary | null {
  if (!summary) return null;

  return {
    ...summary,
    opening_amount: toNumber(summary.opening_amount),
    customer_payments: toNumber(summary.customer_payments),
    customer_refunds: toNumber(summary.customer_refunds),
    correction_reversals: toNumber(summary.correction_reversals),
    correction_replacements: toNumber(summary.correction_replacements),
    adjustments: toNumber(summary.adjustments),
    expense_amount: toNumber(summary.expense_amount),
    transfers: toNumber(summary.transfers),
    other_movements: toNumber(summary.other_movements),
    movement_amount: toNumber(summary.movement_amount),
    expected_amount: toNumber(summary.expected_amount),
    actual_amount: toNumber(summary.actual_amount),
    variance_amount: toNumber(summary.variance_amount),
    ledger_balance: toNumber(summary.ledger_balance),
    current_ledger_balance: toNumber(summary.current_ledger_balance),
    boundary_at: toDate(summary.boundary_at)
  };
}

export function normalizeMoneyDayPaymentTypeSummary(
  rows?: MoneyDayPaymentTypeSummary[] | null
): MoneyDayPaymentTypeSummary[] {
  return (rows || []).map((row) => ({
    ...row,
    customer_payments: toNumber(row?.customer_payments),
    customer_payment_count: toOptionalNumber(row?.customer_payment_count),
    customer_payment_count_available:
      row?.customer_payment_count_available === undefined
        ? row?.customer_payment_count !== null && row?.customer_payment_count !== undefined
        : Boolean(row.customer_payment_count_available),
    customer_refunds: toNumber(row?.customer_refunds),
    customer_refund_count: toOptionalNumber(row?.customer_refund_count),
    customer_refund_count_available:
      row?.customer_refund_count_available === undefined
        ? row?.customer_refund_count !== null && row?.customer_refund_count !== undefined
        : Boolean(row.customer_refund_count_available),
    correction_reversals: toNumber(row?.correction_reversals),
    correction_reversal_count: toNumber(row?.correction_reversal_count),
    correction_replacements: toNumber(row?.correction_replacements),
    correction_replacement_count: toNumber(row?.correction_replacement_count),
    unclassified_movement_count: toNumber(row?.unclassified_movement_count),
    adjustments: toNumber(row?.adjustments),
    transfers: toNumber(row?.transfers),
    other_movements: toNumber(row?.other_movements),
    net_amount: toNumber(row?.net_amount),
    movement_amount: toNumber(row?.movement_amount),
    source_count: toNumber(row?.source_count),
    reconciliation_requires_review: Boolean(row?.reconciliation_requires_review),
    reconciliation_review_reasons: Array.isArray(row?.reconciliation_review_reasons)
      ? row.reconciliation_review_reasons.map((reason) => String(reason))
      : [],
    reconciliation_warnings: Array.isArray(row?.reconciliation_warnings)
      ? row.reconciliation_warnings.map((warning) => String(warning))
      : [],
    container_ids: Array.isArray(row?.container_ids)
      ? row.container_ids.map((id) => toNumber(id)).filter(Boolean)
      : [],
    container_count: toNumber(row?.container_count)
  }));
}

export function normalizeTimeline(timeline?: MoneyEvent[] | null): MoneyEvent[] | undefined {
  if (!timeline?.length) return undefined;
  return timeline.map((item) => normalizeMoneyEvent(item));
}

export function normalizeMoneyCollection(collection: MoneyCollection): MoneyCollection {
  return {
    ...collection,
    accepted_cash_amount: toNumber(collection?.accepted_cash_amount),
    float_left_in_cashbox: toNumber(collection?.float_left_in_cashbox),
    collected_to_safe: toNumber(collection?.collected_to_safe),
    collection_variance: toNumber(collection?.collection_variance),
    accepted_at: toDate(collection?.accepted_at),
    created_at: toDate(collection?.created_at),
    timeline: normalizeTimeline(collection?.timeline)
  };
}

export function normalizeMoneyDay(day: MoneyDay): MoneyDay {
  return {
    ...day,
    cashbox_id:
      day?.cashbox_id !== undefined && day?.cashbox_id !== null
        ? toNumber(day.cashbox_id)
        : day?.cashboxId !== undefined && day?.cashboxId !== null
        ? toNumber(day.cashboxId)
        : day?.cashbox_id,
    closed_ledger_entry_id:
      day?.closed_ledger_entry_id !== undefined && day?.closed_ledger_entry_id !== null
        ? toNumber(day.closed_ledger_entry_id)
        : day?.closedLedgerEntryId !== undefined && day?.closedLedgerEntryId !== null
        ? toNumber(day.closedLedgerEntryId)
        : day?.closed_ledger_entry_id,
    business_date: toDate(day?.business_date),
    opened_at: toDate(day?.opened_at),
    closed_at: toDate(day?.closed_at),
    current_expected_total: toNumber(day?.current_expected_total),
    cashbox_balance:
      day?.cashbox_balance === undefined || day?.cashbox_balance === null
        ? day?.cashbox_balance
        : toNumber(day.cashbox_balance),
    opening_cash_amount: toNumber(day?.opening_cash_amount ?? day?.openingCashAmount),
    cash_shift_movement: toNumber(day?.cash_shift_movement),
    cash_carry_amount: toNumber(day?.cash_carry_amount),
    cash_summary: normalizeMoneyDayCashSummary(day?.cash_summary),
    payment_type_summary: normalizeMoneyDayPaymentTypeSummary(day?.payment_type_summary),
    latest_reconciliation_id: toOptionalNumber(day?.latest_reconciliation_id),
    terminal_reconciliation_expected:
      day?.terminal_reconciliation_expected === undefined
        ? undefined
        : (day.terminal_reconciliation_expected || []).map((line) =>
            normalizeTerminalReconciliationLine(line)
          ),
    latest_reconciliation:
      day?.latest_reconciliation === undefined
        ? undefined
        : day.latest_reconciliation
        ? normalizeReconciliationSubmission(day.latest_reconciliation)
        : null,
    reconciliation_history:
      day?.reconciliation_history === undefined
        ? undefined
        : (day.reconciliation_history || []).map((submission) =>
            normalizeReconciliationSubmission(submission)
          ),
    totals: {
      expected_amount: toNumber(day?.totals?.expected_amount),
      actual_amount: toNumber(day?.totals?.actual_amount),
      variance_amount: toNumber(day?.totals?.variance_amount)
    },
    lines: (day?.lines || []).map((line) => ({
      ...line,
      expected_amount: toNumber(line?.expected_amount),
      actual_amount: toNumber(line?.actual_amount),
      variance_amount: toNumber(line?.variance_amount),
      expected_payment_amount: toOptionalNumber(line?.expected_payment_amount),
      actual_payment_amount: toOptionalNumber(line?.actual_payment_amount),
      payment_variance_amount: toOptionalNumber(line?.payment_variance_amount),
      expected_payment_count: toOptionalNumber(line?.expected_payment_count),
      actual_payment_count: toOptionalNumber(line?.actual_payment_count),
      payment_variance_count: toOptionalNumber(line?.payment_variance_count),
      expected_refund_amount: toOptionalNumber(line?.expected_refund_amount),
      actual_refund_amount: toOptionalNumber(line?.actual_refund_amount),
      refund_variance_amount: toOptionalNumber(line?.refund_variance_amount),
      expected_refund_count: toOptionalNumber(line?.expected_refund_count),
      actual_refund_count: toOptionalNumber(line?.actual_refund_count),
      refund_variance_count: toOptionalNumber(line?.refund_variance_count)
    })),
    current_expected_lines: (day?.current_expected_lines || []).map((line) => ({
      ...line,
      expected_amount: toNumber(line?.expected_amount),
      actual_amount: toNumber(line?.actual_amount),
      variance_amount: toNumber(line?.variance_amount),
      expected_payment_amount: toOptionalNumber(line?.expected_payment_amount),
      actual_payment_amount: toOptionalNumber(line?.actual_payment_amount),
      payment_variance_amount: toOptionalNumber(line?.payment_variance_amount),
      expected_payment_count: toOptionalNumber(line?.expected_payment_count),
      actual_payment_count: toOptionalNumber(line?.actual_payment_count),
      payment_variance_count: toOptionalNumber(line?.payment_variance_count),
      expected_refund_amount: toOptionalNumber(line?.expected_refund_amount),
      actual_refund_amount: toOptionalNumber(line?.actual_refund_amount),
      refund_variance_amount: toOptionalNumber(line?.refund_variance_amount),
      expected_refund_count: toOptionalNumber(line?.expected_refund_count),
      actual_refund_count: toOptionalNumber(line?.actual_refund_count),
      refund_variance_count: toOptionalNumber(line?.refund_variance_count)
    })),
    collection: day?.collection ? normalizeMoneyCollection(day.collection) : null,
    timeline: normalizeTimeline(day?.timeline),
    post_collection_corrections: normalizeTimeline(day?.post_collection_corrections)
  };
}

export function normalizeMoneyAdjustment(adjustment: MoneyAdjustment): MoneyAdjustment {
  return {
    ...adjustment,
    value: toNumber(adjustment?.value),
    date: toDate(adjustment?.date),
    business_date: toDate(adjustment?.business_date),
    created_at: toDate(adjustment?.created_at),
    timeline: normalizeTimeline(adjustment?.timeline)
  };
}

export function normalizeMoneyTransfer(transfer: MoneyTransfer): MoneyTransfer {
  return {
    ...transfer,
    value: toNumber(transfer?.value),
    date: toDate(transfer?.date),
    business_date: toDate(transfer?.business_date),
    created_at: toDate(transfer?.created_at),
    timeline: normalizeTimeline(transfer?.timeline)
  };
}

export function normalizeMoneyContainer(container: MoneyContainer | Bank): MoneyContainer {
  return {
    ...container,
    balance: toNumber(container?.balance),
    warehouse: container?.warehouse || null,
    ownerUser: container?.ownerUser || null,
    isOperational:
      container?.isOperational === undefined || container?.isOperational === null
        ? true
        : Boolean(container.isOperational),
    isCollecting: Boolean(container?.isCollecting)
  };
}
